import { isForwardMessage, type Msg, type Session } from '@halo/chat-sdk-react';
import { Center } from '@chakra-ui/react';
import { ItemExpireContext as MessageExpireContext, MessageMetaRoot } from '@halo/ui-chat-react';
import { LottieRender } from '@halo/ui-chat-react';
import { useStaticAppStore } from '@/store/client';
import { Emoji } from '@/components/Emoji';
import {
  MsgAvatar,
  MsgBody,
  MsgBubble,
  MsgExpireAnimate,
  MsgFooter,
  MsgFooterReplies,
  MsgForwardFrom,
  MsgHeader,
  MsgItem,
  MsgMenuTrigger,
  MsgReadStatus,
} from '../../msg-parts';

// Message
export interface MsgStickerProps {
  session: Session;
  message: Msg;
}

export const MsgSticker = memo(function MsgSticker({ session, message }: MsgStickerProps) {
  const { client } = useStaticAppStore();

  const isForward = useMemo(() => isForwardMessage(message.msg_action), [message.msg_action]);

  const bubbleProps = useMemo(() => (isForward ? undefined : { p: '0' }), [isForward]);

  const getStickerData = useCallback(async () => {
    return client?.Api.emoji
      .getStickerData({
        sticker_id: message.payload.id,
        category: message?.payload?.stickerSetInfo?.category,
      })
      .then((res) => {
        return res.render_data;
      });
  }, [client, message.payload.id, message?.payload?.stickerSetInfo?.category]);

  return (
    <MsgItem message={message}>
      <MessageExpireContext message={message}>
        <MsgAvatar message={message} session={session} />
        <MsgBody message={message}>
          <MsgHeader message={message} session={session} />
          <MsgMenuTrigger message={message}>
            <MsgExpireAnimate session={session} message={message}>
              <MsgBubble message={message} bg="transparent" {...bubbleProps}>
                {message.msg_ext?.forwardUid && (
                  <MsgForwardFrom forwardUid={message.msg_ext.forwardUid} />
                )}
                <LottieRender
                  size="200px"
                  lottieOptions={{
                    autoplay: true,
                    loop: true,
                  }}
                  getData={getStickerData}
                />
                <MessageMetaRoot>
                  <Emoji.MessageMeta chat_id={session.chat_id} message={message} />
                  <Center marginLeft="auto" gap="1.5">
                    <MsgReadStatus message={message} session={session} />
                  </Center>
                </MessageMetaRoot>
              </MsgBubble>
            </MsgExpireAnimate>
          </MsgMenuTrigger>
          <MsgFooter message={message}>
            <MsgFooterReplies message={message} />
          </MsgFooter>
        </MsgBody>
      </MessageExpireContext>
    </MsgItem>
  );
});
